import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../../utils/functions";
import DashboardSearchFilters from "../../common/DashboardSearchFilters";

// Import implementation components
import ImplementationSummary from "./ImplementationSummary";
import StatusDistributionChart from "./StatusDistributionChart";
import VerificationStatus from "./VerificationStatus";
import DistrictPerformance from "./DistrictPerformance";
import HandoverStatus from "./HandoverStatus";
import ConditionAssessment from "./ConditionAssessment";
import ProgressTracking from "./ProgressTracking";
import { hasData } from "../../common/NoData";

const ImplementationDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const canViewImplementation = permissions.includes("dashboard-household");

  const [implementationData, setImplementationData] = useState({
    overallStatus: null,
    statusDistribution: null,
    verificationStatus: null,
    districtPerformance: null,
    handoverStatus: null,
    conditionAssessment: null,
    progressTracking: null,
  });

  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
  });

  const [loading, setLoading] = useState({
    overallStatus: false,
    statusDistribution: false,
    verificationStatus: false,
    districtPerformance: false,
    handoverStatus: false,
    conditionAssessment: false,
    progressTracking: false,
  });
  useEffect(() => {
    fetchAllImplementationData();
  }, [filters]);
  const fetchAllImplementationData = async () => {
    if (!canViewImplementation) {
      console.warn(
        "User doesn't have permission to view implementation dashboard"
      );
      return;
    }
    setLoading({
      overallStatus: true,
      statusDistribution: true,
      verificationStatus: true,
      districtPerformance: true,
      handoverStatus: true,
      conditionAssessment: true,
      progressTracking: true,
    });

    try {
      const endpoints = [
        {
          key: "overallStatus",
          url: "overall-status",
        },
        {
          key: "statusDistribution",
          url: "status-distribution",
        },
        {
          key: "verificationStatus",
          url: "verification-status",
        },
        {
          key: "districtPerformance",
          url: "district-performance",
        },
        {
          key: "handoverStatus",
          url: "handover-status",
        },
        {
          key: "conditionAssessment",
          url: "condition-assessment",
        },
        {
          key: "progressTracking",
          url: "progress-tracking",
        },
      ];

      const promises = endpoints.map(async ({ key, url }) => {
        try {
          const response = await fetchData(
            url,
            "GET",
            null,
            {},
            filters,
            navigate,
            "/implementation-dashboard",
            dispatch
          );

          if (response.success) {
            setImplementationData((prev) => ({
              ...prev,
              [key]: response.data || response.data?.data || {},
            }));
          } else {
            console.error(`Failed to fetch ${key}:`, response.message);
            setImplementationData((prev) => ({
              ...prev,
              [key]: {},
            }));
          }
        } catch (error) {
          console.error(`Error fetching ${key}:`, error);
          setImplementationData((prev) => ({
            ...prev,
            [key]: {},
          }));
        } finally {
          setLoading((prev) => ({
            ...prev,
            [key]: false,
          }));
        }
      });

      await Promise.all(promises);
    } catch (error) {
      console.error("Error fetching implementation dashboard data:", error);
      setLoading({
        overallStatus: false,
        statusDistribution: false,
        verificationStatus: false,
        districtPerformance: false,
        handoverStatus: false,
        conditionAssessment: false,
        progressTracking: false,
      });
    }
  };

  const handleFilterChange = (appliedFilters) => {
    setFilters(appliedFilters);
  };

  const resetFilters = () => {
    setFilters({
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
    });
  };
  const isLoading = Object.values(loading).some((status) => status);

  if (!canViewImplementation) {
    return (
      <div className="w-full px-4 bg-gray-50">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center mt-4">
          <svg
            className="w-12 h-12 text-yellow-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-yellow-700 mb-2 text-balance">
            Access Denied
          </h3>
          <p className="text-yellow-600">
            You don't have permission to view the Implementation Dashboard.
            Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 bg-gray-50">
      <div className="w-full flex flex-col justify-between mb-4">
        <DashboardSearchFilters
          accordionTitle="Implementation Filters"
          defaultExpanded={false}
          fetchOnChange={false}
          apiRoutes={{
            districts: "districts",
            tehsils: "tehsils",
            ucs: "ucs",
            dehs: "dehs",
            villages: "villages",
          }}
          dateFields={["created_at", "updated_at", "implementation_date"]}
          onApply={handleFilterChange}
        />
      </div>
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-b-2 border-teal-700 rounded-full mx-auto"></div>
          <p className="mt-2">Loading Implementation Dashboard Data...</p>
        </div>
      )}
      {!isLoading && (() => {
        const has = {
          postImplementation: hasData(
            implementationData.progressTracking?.criticalStagesCompletion,
            implementationData.conditionAssessment?.conditionAssessment
          ),
          districts:
            (implementationData.districtPerformance?.districts || []).length > 0 ||
            (implementationData.districtPerformance?.villages || []).length > 0,
        };
        return (
        <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5 py-6">
          <div className="space-y-6">
            <div>
              {/* <h3 className="text-xl font-bold text-gray-800 text-balance">
                Implementation Overview
              </h3> */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
                <div>
                  <ImplementationSummary
                    data={{
                      ...implementationData.overallStatus,
                      statusDistribution: implementationData.statusDistribution,
                    }}
                    loading={loading.overallStatus}
                  />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-700  text-center text-balance">
                    Verification Status
                  </h4>
                  <VerificationStatus
                    data={implementationData.verificationStatus}
                    loading={loading.verificationStatus}
                  />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-700 text-center text-balance">
                    Handover Status
                  </h4>
                  <HandoverStatus
                    data={implementationData.handoverStatus}
                    loading={loading.handoverStatus}
                  />
                </div>
              </div>
            </div>

            {/* <hr className="border-t-2 border-dashed border-teal-700 my-4" /> */}

            {/* 2. Three Graphs in Single Row */}
            {/* <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4 text-balance">
                Implementation Analysis
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6"> */}
            {/* Status Distribution Chart */}
            {/* <div className="lg:col-span-1">
              <h4 className="text-lg font-semibold text-gray-700 mb-3 text-center text-balance">
                Status Distribution
              </h4>
              <StatusDistributionChart
                data={implementationData.statusDistribution}
                loading={loading.statusDistribution}
              />
            </div> */}

            {/* <div className="lg:col-span-2">
              <h4 className="text-lg font-semibold text-gray-700 mb-3 text-center text-balance">
                Progress Tracking
              </h4>
              <ProgressTracking
                data={implementationData.progressTracking}
                loading={loading.progressTracking}
              />
            </div> */}
            {/* </div>
            </div>  */}

            {/* <hr className="border-t-2 border-dashed border-teal-700 my-4" /> */}

            {/* 4. Post-Implementation Status */}
            {has.postImplementation && (<>
            <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
            <div>
              {/* <h3 className="text-xl font-bold text-gray-800 mb-4 text-balance">
                Post-Implementation Status
              </h3> */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* This HandoverStatus is redundant now, remove or keep as needed */}
                {/* <HandoverStatus
                  data={implementationData.handoverStatus}
                  loading={loading.handoverStatus}
                /> */}
                <div className="bg-white rounded-lg ">
                  {/* <h4 className="text-lg font-semibold text-gray-700 mb-3 text-center text-balance">
                    Progress Tracking
                  </h4> */}
                  <ProgressTracking
                    data={implementationData.progressTracking}
                    loading={loading.progressTracking}
                  />
                </div>
                <ConditionAssessment
                  data={implementationData.conditionAssessment}
                  loading={loading.conditionAssessment}
                />
              </div>
            </div>
            </>)}

            {/* Data Refresh Button */}
            {/* <div className="text-center pt-6 border-t border-gray-200">
              <button
                onClick={fetchAllImplementationData}
                className="px-6 py-3 bg-[#137e81] text-white rounded-lg hover:bg-[#0f6b6d] transition-colors font-medium"
              >
                Refresh Dashboard Data
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div> */}
          </div>
          {/* 3. District Performance */}
          {has.districts && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4" />
          <div>
            {/* <h3 className="text-xl font-bold text-gray-800 text-balance"> */}
            {/* Performance Analysis */}
            {/* Village-wise Implementation Performance
            </h3> */}
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
              <DistrictPerformance
                data={implementationData.districtPerformance}
                loading={loading.districtPerformance}
              />
            </div>
          </div>
          </>)}
        </div>
        );
      })()}
    </div>
  );
};

export default ImplementationDashboard;
